/**
 * Validates a tenant ID by making a request to Microsoft's OpenID configuration endpoint
 * Returns the OpenID configuration if valid, or null if invalid
 */
export interface OpenIDConfiguration {
  token_endpoint: string;
  authorization_endpoint: string;
  issuer: string;
  jwks_uri: string;
  // Other fields could be added as needed
}

/**
 * Validates whether a tenant ID is valid by attempting to fetch
 * the OpenID configuration from Microsoft
 *
 * @param tenantId The Microsoft tenant ID to validate
 * @returns The OpenID configuration if valid, null if invalid
 */
export const validateTenantId = async (
  tenantId: string
): Promise<OpenIDConfiguration | null> => {
  try {
    // OpenID: https://learn.microsoft.com/en-us/entra/identity-platform/v2-protocols-oidc
    const endpoint = `https://login.microsoftonline.com/${tenantId}/.well-known/openid-configuration`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as Partial<OpenIDConfiguration>;

    // Bail if required keys are missing or obviously wrong
    if (
      !data.issuer?.includes(tenantId) ||
      !data.token_endpoint ||
      !data.authorization_endpoint
    ) {
      return null;
    }

    return data as OpenIDConfiguration;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error validating tenant ID:', error);
    return null;
  }
};
